import type {
  AmplifierData,
  AmplifierProjectData,
  BandParam,
  HamlibControlRequestMessage,
  ProgramStatusData,
  ProgramStatusMessage,
  RemoteControlData,
  RemoteControlEngParams,
  RemoteControlMessage,
  Vgs,
} from './amplifier';
import { parseAntennaConfigFromRemote } from './antennaConfig';
import { swrRawToDisplay, VGS_VOLT_MIN, vgsRawToVolts } from './engParamsScale';
import { parseUpdateableVersion } from './updateVersionUtils';

const EMPTY_VGS: Vgs = {
  frequency: 0,
  vgsPower: VGS_VOLT_MIN,
  inputPositive: 0,
  inputNegative: 0,
  outputPositive: 0,
  outputNegative: 0,
  ampInputPowerCmp: 0,
  ampOutputPowerCmp: 0,
};

export function createEmptyBandParam(): BandParam {
  return {
    vgs1: { ...EMPTY_VGS },
    vgs2: { ...EMPTY_VGS },
    vgs3: { ...EMPTY_VGS },
    vgs4: { ...EMPTY_VGS },
    vgs5: { ...EMPTY_VGS },
    vgs6: { ...EMPTY_VGS },
    vgs7: { ...EMPTY_VGS },
    vgs8: { ...EMPTY_VGS },
    vgs9: { ...EMPTY_VGS },
    vgs10: { ...EMPTY_VGS },
    vgs11: { ...EMPTY_VGS },
    vgs12: { ...EMPTY_VGS },
    vgs13: { ...EMPTY_VGS },
    vgs14: { ...EMPTY_VGS },
    vgs15: { ...EMPTY_VGS },
    vgs16: { ...EMPTY_VGS },
    vgs17: { ...EMPTY_VGS },
  };
}

export function createEmptyAmplifierProjectData(): AmplifierProjectData {
  return {
    voltageCalibrationCoefficient: 0,
    currentCalibrationCoefficient: 0,
    frequencyMeasurementCalibrationCoefficient: 0,
    vgsCalibrationCoefficient: 0,
    exdacRefvCalibrationMv: 0,
    protectionVoltage: 0,
    protectionCurrent: 0,
    protectionTemperature: 0,
    protectionInputPowerPositive: 0,
    protectionInputPowerNegative: 0,
    protectionOutputPowerPositive: 0,
    protectionOutputPowerNegative: 0,
    protectionRf: 0,
    protectionInSwr: 0,
    protectionOutSwr: 0,
    protectionFrequencyError: 0,
    rfTuneValue: 0,
    powerTriggerPttVoltage: 0,
    protectMask: 0,
    inputPowerPositiveCompareVoltage: 0,
    inputPowerNegativeCompareVoltage: 0,
    powerInputPowerPositiveCompareVoltage: 0,
    powerOutputPowerPositiveCompareVoltage: 0,
    outputPowerPositiveCompareVoltage: 0,
    outputPowerNegativeCompareVoltage: 0,
    overCurrentCompareVoltage: 0,
    bandParam: createEmptyBandParam(),
    currentBandNo: 0,
  };
}

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function str(value: unknown, fallback?: string): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  return fallback;
}

function arrayOfNumbers(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((item) => num(item)).filter((item) => Number.isFinite(item));
}

/** band_voltage_freq 可为 Hz 数字或展示用字符串（如 "1.800 MHz–2 MHz"） */
function arrayOfBandVoltageFreq(value: unknown): Array<number | string> {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === 'number' && Number.isFinite(item)) return item;
    if (typeof item === 'string') return item;
    if (item != null) return String(item);
    return '';
  });
}

export function isRemoteControlError(data: unknown): boolean {
  if (data == null || typeof data !== 'object') return false;
  return (data as { result?: unknown }).result === 'error';
}

export function mapRemoteControlToAmplifierData(
  msg: RemoteControlMessage,
  previous?: AmplifierData | null,
): AmplifierData {
  const rc = msg.data ?? {};
  const base = previous ?? ({} as Partial<AmplifierData>);

  return {
    _source: 'remote_control',
    triggerMode: num(rc.trigger_mode, base.triggerMode),
    tuneMode: num(rc.tune_mode, base.tuneMode),
    power: num(rc.pa_switch, base.power),
    frequency: num(rc.work_freq, base.frequency),
    inputAntenna: num(rc.in_antenna_sel ?? rc.input_ant, base.inputAntenna),
    outputAntenna: num(rc.out_antenna_sel ?? rc.output_ant, base.outputAntenna),
    inputAttenuator: num(rc.attenuator_sel ?? rc.input_att, base.inputAttenuator),
    attenuatorList: Array.isArray(rc.attenuator_list)
      ? [...rc.attenuator_list]
      : base.attenuatorList,
    alarmWord: num(rc.alarm_word, base.alarmWord),
    currentVoltage: num(rc.current_voltage, base.currentVoltage),
    currentCurrent: num(rc.current_current, base.currentCurrent),
    currentTemperature: num(rc.current_temperature, base.currentTemperature),
    inputPowerPositive: num(rc.in_fwd_power, base.inputPowerPositive),
    inputPowerNegative: num(rc.in_rev_power, base.inputPowerNegative),
    inputRf: num(rc.in_swr, base.inputRf),
    outputPowerPositive: num(rc.out_fwd_power, base.outputPowerPositive),
    outputPowerNegative: num(rc.out_rev_power, base.outputPowerNegative),
    outputRf: num(rc.out_swr, base.outputRf),
    frequencyMeasurement: num(rc.work_freq_measure, base.frequencyMeasurement),
    filterSel: str(rc.filter_sel, base.filterSel),
    tuningCapacitor: base.tuningCapacitor ?? 0,
    tuningInductor: base.tuningInductor ?? 0,
    loadResistance: base.loadResistance ?? 0,
    filter: base.filter ?? 0,
    ptt: num(rc.ptt_status, base.ptt ?? 0),
    bandVoltage: num(rc.band_voltage_select, base.bandVoltage),
    voltageCalibrationCoefficient: base.voltageCalibrationCoefficient ?? 0,
    currentCalibrationCoefficient: base.currentCalibrationCoefficient ?? 0,
    frequencyMeasurementCalibrationCoefficient:
      base.frequencyMeasurementCalibrationCoefficient ?? 0,
    protectionVoltage: base.protectionVoltage ?? 0,
    protectionCurrent: base.protectionCurrent ?? 0,
    protectionTemperature: base.protectionTemperature ?? 0,
    protectionInputPowerPositive: base.protectionInputPowerPositive ?? 0,
    protectionInputPowerNegative: base.protectionInputPowerNegative ?? 0,
    protectionOutputPowerPositive: base.protectionOutputPowerPositive ?? 0,
    protectionOutputPowerNegative: base.protectionOutputPowerNegative ?? 0,
    protectionRf: base.protectionRf ?? 0,
    protectionFrequencyError: base.protectionFrequencyError ?? 0,
    rfTuneValue: base.rfTuneValue ?? 0,
    powerTriggerPttVoltage: base.powerTriggerPttVoltage ?? 0,
    inputPowerPositiveCompareVoltage: base.inputPowerPositiveCompareVoltage ?? 0,
    inputPowerNegativeCompareVoltage: base.inputPowerNegativeCompareVoltage ?? 0,
    powerInputPowerPositiveCompareVoltage: base.powerInputPowerPositiveCompareVoltage ?? 0,
    powerOutputPowerPositiveCompareVoltage: base.powerOutputPowerPositiveCompareVoltage ?? 0,
    outputPowerPositiveCompareVoltage: base.outputPowerPositiveCompareVoltage ?? 0,
    outputPowerNegativeCompareVoltage: base.outputPowerNegativeCompareVoltage ?? 0,
    overCurrentCompareVoltage: base.overCurrentCompareVoltage ?? 0,
    bandParam: base.bandParam ?? createEmptyBandParam(),
    antennaConfig: parseAntennaConfigFromRemote(
      rc.antenna_name,
      rc.antenna_freq_start,
      rc.antenna_freq_end,
      base.antennaConfig,
    ) ?? base.antennaConfig,
  };
}

export function mapRemoteControlToProgramStatus(msg: RemoteControlMessage): ProgramStatusMessage {
  const rc = msg.data ?? {};
  const bandVoltageFreq = arrayOfBandVoltageFreq(rc.band_voltage_freq);
  const mcuReconnect = bool(rc.mcu_reconnection);

  const data: ProgramStatusData = {
    freq_mode: num(rc.freq_mode),
    freq_band_name: Array.isArray(rc.freq_band_name) ? [...rc.freq_band_name] : [],
    freq_band: Array.isArray(rc.freq_band) ? [...rc.freq_band] : [],
    freq_band_select: rc.freq_band_select == null ? null : num(rc.freq_band_select),
    band_voltage: Array.isArray(rc.band_voltage) ? [...rc.band_voltage] : [],
    band_voltage_select: rc.band_voltage_select == null ? null : num(rc.band_voltage_select),
    band_voltage_name: Array.isArray(rc.band_voltage_name) ? [...rc.band_voltage_name] : [],
    band_voltage_freq: bandVoltageFreq,
    current_band_voltage:
      rc.current_band_voltage !== undefined && rc.current_band_voltage !== null
        ? num(rc.current_band_voltage)
        : undefined,
    hamlib_connection: bool(rc.hamlib_connection),
    serial_connection: bool(rc.serial_connection),
    output_ant_auto: bool(rc.output_ant_auto),
    firmware_version: rc.firmware_version ?? '',
    firmware_updateable: parseUpdateableVersion(rc.firmware_updateable),
    software_version: rc.software_version ?? '',
    software_updateable: parseUpdateableVersion(rc.software_updateable),
    serial_port: rc.mcu_serial_port ?? '',
    mqtt_ip: rc.mqtt_ip ?? '',
    mqtt_port: num(rc.mqtt_port),
    mqtt_clientid: rc.mqtt_clientid ?? '',
    mqtt_username: rc.mqtt_username ?? '',
    mqtt_password: rc.mqtt_password ?? '',
    mqtt_connection: bool(rc.mqtt_connection),
    mcu_reconnection: mcuReconnect,
    language:
      typeof rc.language === 'string' && rc.language.trim() !== ''
        ? (rc.language as ProgramStatusData['language'])
        : undefined,
  };

  return {
    msg_id: msg.msg_id,
    cmd: 'update_program_status',
    timestamp: msg.timestamp,
    data,
  };
}

/** 合并 program_status，保留 remote_control 已上报的连接态字段 */
export function mergeProgramStatusMessage(
  prev: ProgramStatusMessage | null,
  incoming: ProgramStatusMessage,
): ProgramStatusMessage {
  if (!prev?.data || !incoming.data) return incoming;
  const p = prev.data;
  const n = incoming.data;
  return {
    ...incoming,
    data: {
      ...p,
      ...n,
      hamlib_connection:
        typeof n.hamlib_connection === 'boolean' ? n.hamlib_connection : p.hamlib_connection,
      serial_connection:
        typeof n.serial_connection === 'boolean' ? n.serial_connection : p.serial_connection,
      mqtt_connection:
        typeof n.mqtt_connection === 'boolean' ? n.mqtt_connection : p.mqtt_connection,
    },
  };
}

export function mapRemoteControlToHamlibControl(msg: RemoteControlMessage): HamlibControlRequestMessage {
  const rc = msg.data ?? {};
  return {
    msg_id: msg.msg_id,
    cmd: 'set_hamlib',
    timestamp: msg.timestamp,
    data: {
      serial_port_list: Array.isArray(rc.serial_port_list) ? [...rc.serial_port_list] : [],
      serial_port: rc.hamlib_serial_port ?? null,
      serial_boardrate_list: Array.isArray(rc.serial_boardrate_list)
        ? [...rc.serial_boardrate_list]
        : [],
      serial_boardrate: rc.hamlib_baud ?? null,
      hamlib_list: Array.isArray(rc.hamlib_list) ? [...rc.hamlib_list] : [],
      rig: rc.rig ?? null,
    },
  };
}

export function mapEngParamsToProjectData(
  engParams: RemoteControlEngParams | undefined,
  previous?: AmplifierProjectData | null,
): AmplifierProjectData | null {
  if (!engParams) return null;

  const cal = engParams.calibration;
  const prot = engParams.protection;
  const band = engParams.band;
  const hasCal = cal && Object.keys(cal).length > 0;
  const hasProt = prot && Object.keys(prot).length > 0;
  const hasBand = Boolean(band && Object.keys(band).length > 0);
  if (!hasCal && !hasProt && !hasBand) return null;

  const vgsKeys = [
    'vgs1', 'vgs2', 'vgs3', 'vgs4', 'vgs5', 'vgs6', 'vgs7', 'vgs8', 'vgs9',
    'vgs10', 'vgs11', 'vgs12', 'vgs13', 'vgs14', 'vgs15', 'vgs16', 'vgs17',
  ] as const;
  const bandParam = previous?.bandParam ? { ...previous.bandParam } : createEmptyBandParam();
  const rawBandNo = band?.band_no ?? previous?.currentBandNo ?? 0;
  const currentBandNo = Math.min(16, Math.max(0, Math.trunc(num(rawBandNo, 0))));
  if (hasBand && band) {
    const key = vgsKeys[currentBandNo];
    bandParam[key] = {
      frequency: num(band.freq_end, bandParam[key].frequency),
      vgsPower: band.vgs !== undefined ? vgsRawToVolts(num(band.vgs, 0)) : bandParam[key].vgsPower,
      inputPositive: num(band.in_fwd_power_cal, bandParam[key].inputPositive),
      inputNegative: num(band.in_rev_power_cal, bandParam[key].inputNegative),
      outputPositive: num(band.out_fwd_power_cal, bandParam[key].outputPositive),
      outputNegative: num(band.out_rev_power_cal, bandParam[key].outputNegative),
      ampInputPowerCmp: num(band.amp_input_power_cmp, bandParam[key].ampInputPowerCmp),
      ampOutputPowerCmp: num(band.amp_output_power_cmp, bandParam[key].ampOutputPowerCmp),
    };
  }

  return {
    voltageCalibrationCoefficient:
      cal?.voltage_cal ?? previous?.voltageCalibrationCoefficient ?? 0,
    currentCalibrationCoefficient:
      cal?.current_cal ?? previous?.currentCalibrationCoefficient ?? 0,
    frequencyMeasurementCalibrationCoefficient:
      cal?.freq_measure_cal ?? previous?.frequencyMeasurementCalibrationCoefficient ?? 0,
    vgsCalibrationCoefficient: cal?.vgs_cal ?? previous?.vgsCalibrationCoefficient ?? 0,
    exdacRefvCalibrationMv: cal?.exdac_refv_cal_mv ?? previous?.exdacRefvCalibrationMv ?? 0,
    protectionVoltage: prot?.protect_voltage ?? previous?.protectionVoltage ?? 0,
    protectionCurrent: prot?.protect_current ?? previous?.protectionCurrent ?? 0,
    protectionTemperature: prot?.protect_temperature ?? previous?.protectionTemperature ?? 0,
    protectionInputPowerPositive:
      prot?.protect_in_fwd_power ?? previous?.protectionInputPowerPositive ?? 0,
    protectionInputPowerNegative:
      prot?.protect_in_rev_power ?? previous?.protectionInputPowerNegative ?? 0,
    protectionOutputPowerPositive:
      prot?.protect_out_fwd_power ?? previous?.protectionOutputPowerPositive ?? 0,
    protectionOutputPowerNegative:
      prot?.protect_out_rev_power ?? previous?.protectionOutputPowerNegative ?? 0,
    protectionInSwr:
      prot?.in_protect_swr !== undefined
        ? swrRawToDisplay(prot.in_protect_swr)
        : previous?.protectionInSwr ?? previous?.protectionRf ?? 0,
    protectionOutSwr:
      prot?.out_protect_swr !== undefined
        ? swrRawToDisplay(prot.out_protect_swr)
        : previous?.protectionOutSwr ?? previous?.protectionRf ?? 0,
    protectionRf:
      prot?.in_protect_swr !== undefined
        ? swrRawToDisplay(prot.in_protect_swr)
        : previous?.protectionInSwr ?? previous?.protectionRf ?? 0,
    protectionFrequencyError:
      prot?.protect_frequency_error ?? previous?.protectionFrequencyError ?? 0,
    rfTuneValue:
      prot?.swr_tune_value !== undefined
        ? swrRawToDisplay(prot.swr_tune_value)
        : previous?.rfTuneValue ?? 0,
    powerTriggerPttVoltage: prot?.ptt_trigger_power ?? previous?.powerTriggerPttVoltage ?? 0,
    protectMask: prot?.protect_mask ?? previous?.protectMask ?? 0,
    inputPowerPositiveCompareVoltage: previous?.inputPowerPositiveCompareVoltage ?? 0,
    inputPowerNegativeCompareVoltage: previous?.inputPowerNegativeCompareVoltage ?? 0,
    powerInputPowerPositiveCompareVoltage: previous?.powerInputPowerPositiveCompareVoltage ?? 0,
    powerOutputPowerPositiveCompareVoltage: previous?.powerOutputPowerPositiveCompareVoltage ?? 0,
    outputPowerPositiveCompareVoltage: previous?.outputPowerPositiveCompareVoltage ?? 0,
    outputPowerNegativeCompareVoltage: previous?.outputPowerNegativeCompareVoltage ?? 0,
    overCurrentCompareVoltage: previous?.overCurrentCompareVoltage ?? 0,
    bandParam,
    currentBandNo,
  };
}
